import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, STAGE_LABELS } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Ship, CheckCircle2, AlertTriangle, TrendingUp, Plus, ChevronRight, ShieldCheck, Clock } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loadingApprovals, setLoadingApprovals] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).finally(() => setLoading(false));
    if (isAdmin) {
      api.get("/approvals/pending")
        .then((r) => setPendingApprovals(r.data || []))
        .catch(() => setPendingApprovals([]))
        .finally(() => setLoadingApprovals(false));
    } else {
      setLoadingApprovals(false);
    }
  }, [isAdmin]);

  return (
    <div className="p-5 space-y-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-stone-400 font-bold">Overview</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#1F1F1F] mt-1">
          Shipment Dashboard
        </h1>
        <p className="text-stone-500 text-sm mt-1">
          Agri-commodities exports across all stages — track, ship, close.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3" data-testid="kpi-grid">
        <KpiCard
          icon={<Ship className="w-4 h-4" />}
          label="Total"
          value={loading ? "—" : stats?.total_shipments ?? 0}
          tone="brand"
          testId="kpi-total"
        />
        <KpiCard
          icon={<TrendingUp className="w-4 h-4" />}
          label="Active"
          value={loading ? "—" : stats?.active_shipments ?? 0}
          tone="accent"
          testId="kpi-active"
        />
        <KpiCard
          icon={<CheckCircle2 className="w-4 h-4" />}
          label="Completed"
          value={loading ? "—" : stats?.completed_shipments ?? 0}
          tone="muted"
          testId="kpi-completed"
        />
        <KpiCard
          icon={<AlertTriangle className="w-4 h-4" />}
          label="Open Claims"
          value={loading ? "—" : stats?.claims_in_progress ?? 0}
          tone={(stats?.claims_in_progress ?? 0) > 0 ? "warning" : "muted"}
          testId="kpi-claims"
        />
      </div>

      <button
        onClick={() => nav("/app/shipments/new")}
        data-testid="new-shipment-cta"
        className="w-full bg-[#6B0F1A] hover:bg-[#56121C] text-white rounded-2xl p-5 flex items-center justify-between group active:scale-[0.99]"
      >
        <div className="text-left">
          <div className="font-display text-lg font-bold">Start a new shipment</div>
          <div className="text-white/75 text-xs">Begin from Buyer → Closure</div>
        </div>
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center group-hover:bg-white/25">
          <Plus className="w-5 h-5" />
        </div>
      </button>

      {/* Pending approvals (admin only) */}
      {isAdmin && (
        <div data-testid="pending-approvals-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h3 className="font-display text-lg font-bold tracking-tight">
                {user?.is_owner ? "Pending your approval" : "Your submitted approvals"}
              </h3>
              {pendingApprovals.length > 0 && (
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#6B0F1A] text-white text-[10px] font-bold" data-testid="pending-approvals-count">
                  {pendingApprovals.length}
                </span>
              )}
            </div>
          </div>
          {loadingApprovals && <div className="text-sm text-stone-400">Loading…</div>}
          {!loadingApprovals && pendingApprovals.length === 0 && (
            <div className="bg-white border border-dashed border-stone-300 rounded-2xl p-5 text-center" data-testid="pending-approvals-empty">
              <ShieldCheck className="w-6 h-6 mx-auto text-emerald-400" />
              <div className="text-sm font-semibold text-[#1F1F1F] mt-2">All clear</div>
              <div className="text-xs text-stone-500 mt-0.5">
                {user?.is_owner ? "No approvals waiting on you." : "You don't have any submissions awaiting approval."}
              </div>
            </div>
          )}
          <div className="space-y-2" data-testid="pending-approvals-list">
            {pendingApprovals.map((p) => (
              <Link
                key={p.id}
                to={`/app/shipments/${p.id}`}
                data-testid={`pending-approval-${p.id}`}
                className="flex items-center gap-3 bg-amber-50 border border-amber-200 hover:border-amber-400 rounded-2xl p-4 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-[#1F1F1F] truncate">{p.buyer_name}</div>
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-white text-amber-800 border border-amber-300 rounded-full px-2 py-0.5">
                      {STAGE_LABELS[p.pending_approval_stage] || p.pending_approval_stage}
                    </span>
                  </div>
                  <div className="text-xs text-stone-600 truncate font-mono">{p.reference}</div>
                  <div className="text-[11px] text-stone-500 mt-0.5">
                    Submitted {p.approval_submitted_by_name ? `by ${p.approval_submitted_by_name}` : ""}
                    {p.approval_submitted_at && ` · ${new Date(p.approval_submitted_at).toLocaleString()}`}
                  </div>
                  {!p.is_assigned_to_me && p.pending_approval_owner_name && user?.is_owner && (
                    <div className="text-[11px] text-stone-500 mt-0.5">
                      Assigned to <span className="font-semibold">{p.pending_approval_owner_name}</span>
                    </div>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-amber-400" />
              </Link>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-bold tracking-tight">Recent shipments</h3>
          <Link to="/app/shipments" className="text-xs font-semibold text-[#6B0F1A]" data-testid="view-all-shipments">
            View all →
          </Link>
        </div>
        <div className="space-y-2" data-testid="recent-shipments">
          {loading && <div className="text-sm text-stone-400">Loading…</div>}
          {!loading && (stats?.recent_shipments || []).length === 0 && (
            <EmptyState />
          )}
          {(stats?.recent_shipments || []).map((s) => (
            <ShipmentRow key={s.id} s={s} />
          ))}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, tone, testId }) {
  const toneMap = {
    brand: "bg-[#6B0F1A] text-white",
    accent: "bg-[#A8323F] text-white",
    muted: "bg-white text-[#1F1F1F] border border-stone-200",
    warning: "bg-amber-50 text-amber-900 border border-amber-200",
  };
  return (
    <div className={`rounded-2xl p-4 ${toneMap[tone]} hover-lift`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-widest opacity-80">{label}</div>
        <div className="opacity-80">{icon}</div>
      </div>
      <div className="font-display text-3xl font-extrabold mt-2">{value}</div>
    </div>
  );
}

function ShipmentRow({ s }) {
  return (
    <Link
      to={`/app/shipments/${s.id}`}
      className="flex items-center gap-3 bg-white border border-stone-200 hover:border-[#A8323F] rounded-2xl p-4 hover-lift"
      data-testid={`shipment-row-${s.id}`}
    >
      <div className="w-10 h-10 rounded-xl bg-[#F4E8E9] text-[#6B0F1A] flex items-center justify-center">
        <Ship className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-semibold text-[#1F1F1F] truncate">{s.reference}</div>
          <StatusBadge status={s.status} />
        </div>
        <div className="text-xs text-stone-500 truncate">
          {s.buyer_name} · Stage: {STAGE_LABELS[s.current_stage] || s.current_stage}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-stone-300" />
    </Link>
  );
}

export function StatusBadge({ status }) {
  const map = {
    active: "bg-[#F4E8E9] text-[#6B0F1A]",
    completed: "bg-stone-100 text-stone-600",
    claim: "bg-amber-100 text-amber-800",
  };
  const label = { active: "Active", completed: "Completed", claim: "Claim" }[status] || status;
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${map[status] || "bg-stone-100"}`}>
      {label}
    </span>
  );
}

function EmptyState() {
  return (
    <div className="bg-white border border-dashed border-stone-300 rounded-2xl p-8 text-center">
      <Ship className="w-8 h-8 mx-auto text-stone-300" />
      <div className="font-semibold text-[#1F1F1F] mt-3">No shipments yet</div>
      <div className="text-sm text-stone-500 mt-1">Register a buyer and create your first shipment.</div>
    </div>
  );
}
